import {
  Card,
  Row,
  Col,
  Badge,
  ListGroup,
  Spinner,
  Container,
  Form,
  Button,
  ButtonGroup
} from 'react-bootstrap';
import { format } from 'date-fns';
import {
  GET_TASK_BY_ID,
  UPDATE_TASK_STATUS,
  ADD_COMMENT,
  UPDATE_SUBTASK_STATUS,
  DELETE_TASK
} from 'services/api/task';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { GET_JOBSTAGE_QUERY } from 'services/api/jobStage';
import { GET_EMPLOYEES_QUERY } from 'services/api/employee';
import { GET_JOBS_QUERY, Job } from 'services/api/job';
import { Employee } from 'data/users';
import TaskComments from './TaskComments';
import TaskActivityLogsModal from './TaskActivityLogsModal';
import TaskModal from './TaskModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useMemo, useState } from 'react';
import { SubTask } from '../types/formFields';
import { useAccessToken } from 'providers/AccessTokenProvider';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHistory,
  faPenToSquare,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

interface TaskDetailsContentProps {
  taskId: string;
  /** Called after the task is deleted (closes the drawer / navigates away). */
  onClose?: () => void;
}

/**
 * Presentational/stateful body for a single task's details. Shared by the
 * standalone route page (/home/apps/tasks/view/:id) and the global
 * TaskDetailsOffcanvas so both stay in sync. Does NOT render page chrome
 * (breadcrumb) — that belongs to the route wrapper.
 */
const TaskDetailsContent = ({ taskId, onClose }: TaskDetailsContentProps) => {
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState<string>('');
  const [showActivityLogs, setShowActivityLogs] = useState<boolean>(false);
  const [showEdit, setShowEdit] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [updatingSubTaskIndex, setUpdatingSubTaskIndex] = useState<
    number | null
  >(null);
  const { authUser } = useAccessToken();

  const { data, isLoading, error } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => GET_TASK_BY_ID(taskId as string),
    enabled: !!taskId
  });

  const { data: jobStages } = useQuery('jobStages', GET_JOBSTAGE_QUERY, {
    staleTime: 5 * 60 * 1000
  });

  // Options for the inline edit form (mirrors the Tasks page).
  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: GET_EMPLOYEES_QUERY,
    staleTime: 3 * 60 * 60 * 1000,
    select: (response: any) =>
      response.data.map((employee: Employee) => ({
        value: (employee as any).id || employee._id,
        label: `${employee.firstName} ${employee.lastName}`
      }))
  });

  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: GET_JOBS_QUERY,
    staleTime: 3 * 60 * 60 * 1000,
    select: (response: any) =>
      response?.data
        ?.filter(
          (job: Job) =>
            job.jobStage?.name?.toLowerCase() !== 'closed' &&
            job.jobStage?.name?.toLowerCase() !== 'cancelled'
        )
        .map((job: Job) => ({
          value: (job as any).id || job._id,
          label: job.title,
          well: job.well,
          location: job.location,
          customer: job.customer,
          jobNumber: job.jobNumber
        }))
  });

  const stageReminderLabel = useMemo(() => {
    const reminder = (data?.data as any)?.stageReminder;
    if (!reminder) return null;
    if (reminder.triggerType === 'any_stage_change') {
      return 'On any job stage change';
    }
    const names = (reminder.stageIds || []).map((id: string) => {
      const stage = jobStages?.data?.find(s => s.id === id);
      return stage?.name || 'Unknown stage';
    });
    return names.length
      ? `When job enters: ${names.join(', ')}`
      : 'When job enters selected stage(s)';
  }, [data, jobStages]);

  const updateStatusMutation = useMutation({
    mutationFn: ({ status }: { status: string }) =>
      UPDATE_TASK_STATUS({ taskId: taskId as string, status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['filteredTasks'] });
      queryClient.invalidateQueries({ queryKey: ['activityLogs', taskId] });
      queryClient.invalidateQueries({ queryKey: ['activityLogs'] });
      toast.success('Task status updated successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.messages || 'Failed to update status');
    }
  });

  const addCommentMutation = useMutation({
    mutationFn: (text: string) =>
      ADD_COMMENT({
        taskId: taskId as string,
        text,
        userId: ((authUser as any)?.id || authUser?._id) as string
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['filteredTasks'] });
      queryClient.invalidateQueries({ queryKey: ['activityLogs', taskId] });
      queryClient.invalidateQueries({ queryKey: ['activityLogs'] });
      toast.success('Comment added successfully');
      setNewComment('');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.messages || 'Failed to add comment');
    }
  });

  const updateSubTaskMutation = useMutation({
    mutationFn: (params: {
      taskId: string;
      subTaskIndex: number;
      completed: boolean;
      completedAt: string | null;
      completedBy: string | null;
    }) => UPDATE_SUBTASK_STATUS(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['filteredTasks'] });
      queryClient.invalidateQueries({ queryKey: ['activityLogs', taskId] });
      queryClient.invalidateQueries({ queryKey: ['activityLogs'] });
      setUpdatingSubTaskIndex(null);
      toast.success('Subtask status updated successfully');
    },
    onError: (error: any) => {
      setUpdatingSubTaskIndex(null);
      toast.error(
        error?.response?.data?.messages || 'Failed to update subtask status'
      );
    }
  });

  const deleteTaskMutation = useMutation({
    mutationFn: () => DELETE_TASK(taskId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['filteredTasks'] });
      queryClient.invalidateQueries({ queryKey: ['jobTasks'] });
      queryClient.invalidateQueries({ queryKey: ['activityLogs'] });
      toast.success('Task deleted successfully');
      setShowDelete(false);
      onClose?.();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.messages || 'Failed to delete task');
    }
  });

  const userMap = useMemo(() => {
    if (!data?.data) return {};

    const users: Record<string, any> = {};

    if (data.data.creator && typeof data.data.creator === 'object') {
      const creatorId = (data.data.creator as any).id || data.data.creator._id;
      if (creatorId) users[creatorId] = data.data.creator;
    }

    if (data.data.assignedTo && Array.isArray(data.data.assignedTo)) {
      data.data.assignedTo.forEach(user => {
        if (user && typeof user === 'object') {
          const userId = (user as any).id || (user as any)._id;
          if (userId) users[userId] = user;
        }
      });
    }

    return users;
  }, [data]);

  if (isLoading) {
    return (
      <Container
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: '60vh' }}
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Card className="border-danger">
          <Card.Body className="text-center">
            <i className="bi bi-exclamation-triangle-fill text-danger fs-1 mb-3"></i>
            <h4 className="text-danger">Error loading task</h4>
            <p className="text-muted">{error as string}</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container className="py-5">
        <Card className="border-warning">
          <Card.Body className="text-center">
            <i className="bi bi-exclamation-circle-fill text-warning fs-1 mb-3"></i>
            <h4 className="text-warning">Task not found</h4>
            <p className="text-muted">
              The requested task could not be found in the system.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const priorityColorMap: Record<string, string> = {
    high: 'danger',
    medium: 'warning',
    low: 'success'
  };

  const handleStatusUpdate = (status: string) => {
    if (status === data?.data?.status) return;
    updateStatusMutation.mutate({ status });
  };

  const handleAddComment = (e: React.FormEvent) => {
    e.preventDefault();
    if (newComment.trim()) {
      addCommentMutation.mutate(newComment);
    }
  };

  const handleShowActivityLogs = () => {
    setShowActivityLogs(true);
  };

  const handleCloseActivityLogs = () => {
    setShowActivityLogs(false);
  };

  const handleToggleSubTask = (
    index: number,
    event?: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event) {
      event.stopPropagation();
    }

    if (updatingSubTaskIndex !== null) return;

    const subTask = data?.data?.subTask?.[index];
    if (!subTask) return;

    const isCompleted = !subTask.completed;
    const completedAt = isCompleted ? new Date().toISOString() : null;
    const completedBy = isCompleted
      ? (authUser as any)?.id || authUser?._id || null
      : null;

    setUpdatingSubTaskIndex(index);
    updateSubTaskMutation.mutate({
      taskId: taskId as string,
      subTaskIndex: index,
      completed: isCompleted,
      completedAt,
      completedBy
    });
  };

  return (
    <div>
      {/* Task Header Section */}
      <Card className="shadow-sm mb-4 border-0">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="flex-grow-1">
              <h2 className="mb-3 fw-bold">{data.data.task}</h2>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <Badge
                  bg={priorityColorMap[data.data.priority] || 'secondary'}
                  className="text-uppercase"
                >
                  {data.data.priority} priority
                </Badge>

                <div className="ms-2">
                  <span className="text-muted me-2">Status:</span>
                  <ButtonGroup size="sm">
                    <Button
                      variant={
                        data.data.status === 'not started'
                          ? 'secondary'
                          : 'outline-secondary'
                      }
                      onClick={() => handleStatusUpdate('not started')}
                      disabled={updateStatusMutation.isLoading}
                    >
                      Not Started
                    </Button>
                    <Button
                      variant={
                        data.data.status === 'in progress'
                          ? 'info'
                          : 'outline-info'
                      }
                      onClick={() => handleStatusUpdate('in progress')}
                      disabled={updateStatusMutation.isLoading}
                    >
                      In Progress
                    </Button>
                    <Button
                      variant={
                        data.data.status === 'completed'
                          ? 'success'
                          : 'outline-success'
                      }
                      onClick={() => handleStatusUpdate('completed')}
                      disabled={updateStatusMutation.isLoading}
                    >
                      Completed
                    </Button>
                  </ButtonGroup>
                  {updateStatusMutation.isLoading && (
                    <Spinner animation="border" size="sm" className="ms-2" />
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowEdit(true)}
                className="d-flex align-items-center gap-2"
              >
                <FontAwesomeIcon icon={faPenToSquare} />
                Edit
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleShowActivityLogs}
                className="d-flex align-items-center gap-2"
              >
                <FontAwesomeIcon icon={faHistory} />
                Activity Logs
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setShowDelete(true)}
                className="d-flex align-items-center gap-2"
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Main Content */}
      <Row className="g-4">
        {/* Left Column - Description and Subtasks */}
        <Col lg={8}>
          <Card className="shadow-sm h-100">
            <Card.Body className="p-4">
              <h5 className="mb-3 fw-bold d-flex align-items-center">
                <i className="bi bi-card-text me-2"></i>Description
              </h5>
              <div className="p-3 bg-light rounded mb-4">
                <p className="mb-0">
                  {data.data.description || (
                    <span className="text-muted">No description provided</span>
                  )}
                </p>
              </div>

              {data?.data?.subTask && data?.data?.subTask?.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3 fw-bold d-flex align-items-center">
                    <i className="bi bi-list-task me-2"></i>Subtasks
                  </h5>
                  <ListGroup variant="flush">
                    {data?.data?.subTask
                      ?.filter((st: SubTask) => st?.text?.trim() !== '')
                      .map((subTask: SubTask, index: number) => (
                        <ListGroup.Item
                          key={index}
                          className={`d-flex align-items-center p-3 border rounded mb-2 ${
                            subTask.completed ? 'bg-light' : ''
                          }`}
                        >
                          <Form.Check
                            type="checkbox"
                            checked={subTask.completed}
                            onChange={e => handleToggleSubTask(index, e)}
                            className="me-3"
                            disabled={updatingSubTaskIndex === index}
                          />
                          <div className="flex-grow-1">
                            <span
                              className={
                                subTask.completed
                                  ? 'text-decoration-line-through text-muted'
                                  : ''
                              }
                            >
                              {subTask.text}
                            </span>
                            {subTask.completed && subTask.completedAt && (
                              <div className="mt-1">
                                <small className="text-muted">
                                  Completed on{' '}
                                  {format(
                                    new Date(subTask.completedAt),
                                    'MMM dd, yyyy HH:mm'
                                  )}
                                </small>
                              </div>
                            )}
                          </div>
                          {updatingSubTaskIndex === index && (
                            <Spinner animation="border" size="sm" />
                          )}
                        </ListGroup.Item>
                      ))}
                  </ListGroup>
                </div>
              )}

              {/* Add Comment Form */}
              <div className="mt-4">
                <h5 className="mb-3 fw-bold d-flex align-items-center">
                  <i className="bi bi-chat-left-text me-2"></i>Add Comment
                </h5>
                <Form onSubmit={handleAddComment}>
                  <Form.Group className="mb-3">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      placeholder="Write your comment here..."
                    />
                  </Form.Group>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={
                      !newComment.trim() || addCommentMutation.isLoading
                    }
                  >
                    {addCommentMutation.isLoading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Posting...
                      </>
                    ) : (
                      'Post Comment'
                    )}
                  </Button>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column - Task Details */}
        <Col lg={4}>
          <Card className="shadow-sm h-100">
            <Card.Body className="p-4">
              <h5 className="mb-3 fw-bold d-flex align-items-center">
                <i className="bi bi-info-circle me-2"></i>Task Details
              </h5>

              <div className="mb-4">
                <h6 className="text-uppercase text-muted small fw-bold mb-2">
                  Due Date
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-calendar-date text-primary"></i>
                  <span className="fw-bold">
                    {data.data.date
                      ? format(new Date(data.data.date), 'MMMM dd, yyyy')
                      : '—'}
                  </span>
                </div>
                {data.data.time && (
                  <div className="d-flex align-items-center gap-2 mt-1">
                    <i className="bi bi-clock text-primary"></i>
                    <span className="fw-bold">{data.data.time}</span>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <h6 className="text-uppercase text-muted small fw-bold mb-2">
                  Job
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-briefcase text-primary"></i>
                  <span className="fw-bold">
                    {(data.data as any).job
                      ? `${(data.data as any).job.title} (${
                          (data.data as any).job.well || ''
                        })`
                      : 'N/A'}
                  </span>
                </div>
              </div>

              {stageReminderLabel && (
                <div className="mb-4">
                  <h6 className="text-uppercase text-muted small fw-bold mb-2">
                    Stage Reminder
                  </h6>
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-bell text-primary"></i>
                    <span className="fw-bold">{stageReminderLabel}</span>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <h6 className="text-uppercase text-muted small fw-bold mb-2">
                  Created By
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-person text-primary"></i>
                  <span className="fw-bold">
                    {data.data.creator
                      ? `${data.data.creator.firstName} ${data.data.creator.lastName}`
                      : 'Unknown'}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <h6 className="text-uppercase text-muted small fw-bold mb-2">
                  Created At
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <i className="bi bi-clock-history text-primary"></i>
                  <span className="fw-bold">
                    {format(
                      new Date(data.data.createdAt),
                      'MMMM dd, yyyy h:mm a'
                    )}
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Assigned Users Section */}
      <Card className="shadow-sm mt-4">
        <Card.Body className="p-4">
          <h5 className="mb-3 fw-bold d-flex align-items-center">
            <i className="bi bi-people me-2"></i>Assigned To
          </h5>
          {data.data.assignedTo && data.data.assignedTo.length > 0 ? (
            <div className="d-flex flex-wrap gap-3">
              {data.data.assignedTo.map((user: any, index: number) => (
                <div
                  key={index}
                  className="d-flex align-items-center bg-light rounded-pill px-3 py-2"
                >
                  <div
                    className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '32px', height: '32px' }}
                  >
                    {user.firstName.charAt(0)}
                    {user.lastName.charAt(0)}
                  </div>
                  <div className="ms-2">
                    <div className="fw-bold">
                      {user.firstName} {user.lastName}
                    </div>
                    <small className="text-muted">
                      {user.role || 'Team Member'}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-3">
              <i className="bi bi-person-x fs-1 text-muted"></i>
              <p className="text-muted mt-2">No users assigned to this task</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Comments Section */}
      <TaskComments
        comments={(data.data.comments || []).map(comment => ({
          _id: `comment-${Math.random().toString(36).substring(2, 9)}`,
          text: comment.text,
          userId: comment.userId,
          createdAt: comment.createdAt,
          editedAt: comment.editedAt
        }))}
        users={userMap}
      />

      {/* Activity Logs Modal */}
      <TaskActivityLogsModal
        show={showActivityLogs}
        onHide={handleCloseActivityLogs}
        taskId={taskId as string}
        taskName={data.data.task}
      />

      {/* Edit Task Modal */}
      {showEdit && (
        <TaskModal
          show={showEdit}
          handleClose={() => setShowEdit(false)}
          task={data.data as any}
          employees={employees}
          jobs={jobs}
        />
      )}

      {/* Delete confirmation */}
      <DeleteConfirmationModal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        onConfirm={() => deleteTaskMutation.mutate()}
        taskName={data.data.task}
        isDeleting={deleteTaskMutation.isLoading}
      />
    </div>
  );
};

export default TaskDetailsContent;
